import React, { useEffect, useMemo, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Loader2, HardHat, Save, Calculator as CalcIcon, Building2, TrendingUp, Wrench, Package, FileSpreadsheet, ChevronRight, ChevronDown } from 'lucide-react';
import InfoTip from './InfoTip';
import PrintButton from './PrintButton';
import AmenitiesPicker from './AmenitiesPicker';
import DrawingImport from './DrawingImport';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const PHASE_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#0ea5e9', '#6366f1', '#a855f7', '#ec4899'];

// Region → default currency. Mirrors REGION_COST_MULTIPLIERS.currency on the
// backend; selecting a region auto-updates target_currency to the local one.
const REGION_DEFAULT_CURRENCY = {
  us: 'USD', canada: 'CAD', uk: 'GBP', eu: 'EUR', gcc: 'AED',
  india: 'INR', sea: 'SGD', china: 'CNY', latam: 'BRL',
  africa: 'ZAR', australia: 'AUD',
};

const formatCurrency = (amt, symbol = '$') => {
  if (amt === null || amt === undefined || Number.isNaN(amt)) return `${symbol}0`;
  return `${symbol}${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const formatHours = (h) => Number(h || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function ConstructionCalculator({ onCalculated, result: externalResult, onSaved }) {
  const [sectors, setSectors] = useState([]);
  const [phases, setPhases] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [regions, setRegions] = useState([]);
  const [criticalities, setCriticalities] = useState([]);
  const [forex, setForex] = useState([]);

  // Form state
  const [form, setForm] = useState({
    project_name: '',
    sector: 'buildings',
    sub_sector: 'office',
    contract_type: 'lump_sum',
    criticality: 'medium',
    region: 'us',
    city: '',
    target_currency: 'USD',
    project_duration_months: 12,
    annual_inflation_pct: 3.0,
    site_complexity: 'moderate',
    shift_pattern: 'single',
    escalation_policy: 'fixed_price',
    bim_lod: 'LOD300',
    procurement_model: 'traditional',
    granularity: 'area',
    gross_floor_area_m2: 5000,
    linear_length_m: '',
    cost_per_m2_override: '',
    phases_enabled: ['site_prep', 'substructure', 'superstructure', 'envelope', 'mep', 'finishes', 'external_works', 'commissioning'],
    sub_phases_disabled: [],
    amenities: [],
  });

  const [wbsLibrary, setWbsLibrary] = useState({});  // { phase_key: [ {key,label,weight,...} ] }
  const [result, setResult] = useState(externalResult || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync with external result when shell drives it
  useEffect(() => {
    if (externalResult !== undefined && externalResult !== result) setResult(externalResult);
    // eslint-disable-next-line
  }, [externalResult]);

  useEffect(() => {
    Promise.all([
      ConstructionAPI.getSectors(),
      ConstructionAPI.getPhases(),
      ConstructionAPI.getContracts(),
      ConstructionAPI.getRegions(),
      ConstructionAPI.getCriticality(),
      ConstructionAPI.getForex(),
      ConstructionAPI.getWbs(),
    ]).then(([s, p, c, r, cr, fx, wbs]) => {
      setSectors(s.sectors || []);
      setPhases(p.phases || []);
      setContracts(c.contracts || []);
      setRegions(r.regions || []);
      setCriticalities(cr.criticality_levels || []);
      setForex(fx.rates || []);
      // /wbs returns { wbs: { phase_key: [items] } } or { phase_key: [items] }
      setWbsLibrary(wbs?.wbs || wbs || {});
    }).catch(err => {
      console.error(err);
      toast.error('Failed to load construction reference data');
    });
  }, []);

  const currentSector = useMemo(() => sectors.find(s => s.key === form.sector), [sectors, form.sector]);
  const subSectors = currentSector?.sub_sectors || [];
  const currentRegion = useMemo(() => regions.find(r => r.key === form.region), [regions, form.region]);
  const cityList = currentRegion?.cities || [];

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const togglePhase = (key) => {
    setForm(prev => ({
      ...prev,
      phases_enabled: prev.phases_enabled.includes(key)
        ? prev.phases_enabled.filter(p => p !== key)
        : [...prev.phases_enabled, key],
    }));
  };

  const toggleSubPhase = (phaseKey, wbsKey) => {
    const id = `${phaseKey}:${wbsKey}`;
    setForm(prev => ({
      ...prev,
      sub_phases_disabled: prev.sub_phases_disabled.includes(id)
        ? prev.sub_phases_disabled.filter(s => s !== id)
        : [...prev.sub_phases_disabled, id],
    }));
  };

  const applyDrawingResult = (extracted) => {
    setForm(prev => ({
      ...prev,
      gross_floor_area_m2: extracted.gross_floor_area_m2 || prev.gross_floor_area_m2,
      sub_sector: extracted.sub_sector || prev.sub_sector,
    }));
    if (extracted.key_observations?.length) {
      toast.message('Drawing applied', { description: `GFA, sub-sector populated. Review the calculator before computing.` });
    }
  };

  const runCalc = async () => {
    if (!form.project_name.trim()) return toast.error('Please enter a project name');
    if (form.granularity !== 'boq' && !form.gross_floor_area_m2 && !form.linear_length_m) {
      return toast.error('Enter GFA (m²) or linear length (m)');
    }
    try {
      setLoading(true);
      const payload = {
        ...form,
        project_duration_months: Number(form.project_duration_months) || 12,
        annual_inflation_pct: Number(form.annual_inflation_pct) || 0,
        city: form.city || null,
        gross_floor_area_m2: form.gross_floor_area_m2 ? Number(form.gross_floor_area_m2) : null,
        linear_length_m: form.linear_length_m ? Number(form.linear_length_m) : null,
        cost_per_m2_override: form.cost_per_m2_override ? Number(form.cost_per_m2_override) : null,
        amenities: form.amenities || [],
        sub_phases_disabled: form.sub_phases_disabled || [],
      };
      const res = await ConstructionAPI.calculate(payload);
      setResult(res);
      onCalculated?.(res);
      toast.success('Construction estimate calculated');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const saveProject = async () => {
    if (!result) return;
    try {
      setSaving(true);
      const res = await ConstructionAPI.saveProject(result, null);
      onSaved?.(res?.id || result?.id);
      toast.success('Project saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="construction-calculator">
      {/* Inputs */}
      <Card className="border-orange-200/60 bg-gradient-to-br from-orange-50/40 to-amber-50/40">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-600 rounded-lg"><HardHat className="h-5 w-5 text-white" /></div>
            <div>
              <CardTitle className="text-slate-900">Construction Phase Estimator</CardTitle>
              <p className="text-sm text-slate-600">Level of effort, productivity &amp; cost across all 8 construction phases.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Project name</Label>
              <Input value={form.project_name} onChange={e => setField('project_name', e.target.value)} placeholder="e.g. Downtown Tower" data-testid="const-project-name" />
            </div>
            <div>
              <Label>Sector<InfoTip>High-level industry segment (buildings, infrastructure, energy, etc.). Drives the sub-sector list and base $/m² cost.</InfoTip></Label>
              <Select value={form.sector} onValueChange={(v) => {
                const sec = sectors.find(s => s.key === v);
                setForm(prev => ({ ...prev, sector: v, sub_sector: sec?.sub_sectors?.[0]?.key || prev.sub_sector }));
              }}>
                <SelectTrigger data-testid="const-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sectors.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-sector</Label>
              <Select value={form.sub_sector} onValueChange={(v) => setField('sub_sector', v)}>
                <SelectTrigger data-testid="const-sub-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {subSectors.map(ss => <SelectItem key={ss.key} value={ss.key}>{ss.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contract type</Label>
              <Select value={form.contract_type} onValueChange={(v) => setField('contract_type', v)}>
                <SelectTrigger data-testid="const-contract"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {contracts.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Criticality</Label>
              <Select value={form.criticality} onValueChange={(v) => setField('criticality', v)}>
                <SelectTrigger data-testid="const-criticality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {criticalities.map(c => <SelectItem key={c.key} value={c.key}>{c.label} (+{Math.round((c.cost_contingency || 0) * 100)}% contingency)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Region (cost/productivity base)<InfoTip>Sets the regional cost &amp; productivity multipliers. Pick a city below for finer detail.</InfoTip></Label>
              <Select value={form.region} onValueChange={(v) => setForm(prev => ({ ...prev, region: v, city: '', target_currency: REGION_DEFAULT_CURRENCY[v] || prev.target_currency }))}>
                <SelectTrigger data-testid="const-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {regions.map(r => <SelectItem key={r.key} value={r.key}>{r.label} (×{r.cost_mult})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City <span className="text-xs text-slate-500">— optional, finer cost</span></Label>
              <Select value={form.city || '__none'} onValueChange={(v) => setField('city', v === '__none' ? '' : v)}>
                <SelectTrigger data-testid="const-city"><SelectValue placeholder="Use region average" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none">— Use region average —</SelectItem>
                  {cityList.map(c => <SelectItem key={c.key} value={c.key}>{c.label}{c.country ? `, ${c.country}` : ''} (×{c.cost_mult_vs_region})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target currency</Label>
              <Select value={form.target_currency} onValueChange={(v) => setField('target_currency', v)}>
                <SelectTrigger data-testid="const-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {forex.map(f => <SelectItem key={f.currency} value={f.currency}>{f.currency} — {f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration (months)</Label>
              <Input type="number" min="1" value={form.project_duration_months} onChange={e => setField('project_duration_months', e.target.value)} data-testid="const-duration" />
            </div>
            <div>
              <Label>Annual cost inflation (%)<InfoTip>Compounded annually and applied to the project mid-point so the uplift represents the average escalation across the build period.</InfoTip></Label>
              <Input type="number" step="0.1" min="0" value={form.annual_inflation_pct} onChange={e => setField('annual_inflation_pct', e.target.value)} data-testid="const-inflation" placeholder="e.g. 3.5" />
              <p className="text-[11px] text-slate-500 mt-0.5">Applied compounded to project mid-point.</p>
            </div>
            <div>
              <Label>Estimation mode</Label>
              <Select value={form.granularity} onValueChange={(v) => setField('granularity', v)}>
                <SelectTrigger data-testid="const-granularity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="area">Area-based ($/m² defaults)</SelectItem>
                  <SelectItem value="boq">BoQ quantities (pure)</SelectItem>
                  <SelectItem value="hybrid">Hybrid (area + BoQ extras)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.granularity !== 'boq' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Gross Floor Area (m²)<InfoTip>Total enclosed floor area across all storeys, used to drive the area-based cost.</InfoTip></Label>
                <Input type="number" min="0" value={form.gross_floor_area_m2} onChange={e => setField('gross_floor_area_m2', e.target.value)} data-testid="const-gfa" />
              </div>
              <div>
                <Label>Linear length (m) <span className="text-xs text-slate-500">— roads, tunnels, rail</span></Label>
                <Input type="number" min="0" value={form.linear_length_m} onChange={e => setField('linear_length_m', e.target.value)} data-testid="const-length" />
              </div>
              <div>
                <Label>Override $/m² <span className="text-xs text-slate-500">— optional</span><InfoTip>Bypass the sub-sector default $/m² with your own benchmark.</InfoTip></Label>
                <Input type="number" min="0" value={form.cost_per_m2_override} onChange={e => setField('cost_per_m2_override', e.target.value)} data-testid="const-rate-override" />
              </div>
            </div>
          )}

          {/* ---- Project detail (Wave 2) ---- */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid="project-detail-section">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Project detail
              <InfoTip>These five dimensions tune the calculation beyond the sector $/m² baseline. Combined uplift typically lands in the −5% to +28% range.</InfoTip>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <Label>Site complexity<InfoTip>Simple = open greenfield; Very complex = constrained urban / live operational.</InfoTip></Label>
                <Select value={form.site_complexity} onValueChange={v => setField('site_complexity', v)}>
                  <SelectTrigger className="h-9" data-testid="const-site-complexity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">Simple (−5%)</SelectItem>
                    <SelectItem value="moderate">Moderate (0%)</SelectItem>
                    <SelectItem value="complex">Complex (+7%)</SelectItem>
                    <SelectItem value="very_complex">Very complex (+15%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Shift pattern<InfoTip>Driving night/24-7 shifts adds premium pay and supervision overhead.</InfoTip></Label>
                <Select value={form.shift_pattern} onValueChange={v => setField('shift_pattern', v)}>
                  <SelectTrigger className="h-9" data-testid="const-shift-pattern"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single (8h)</SelectItem>
                    <SelectItem value="extended">Extended (10h, +4%)</SelectItem>
                    <SelectItem value="double">Double (16h, +10%)</SelectItem>
                    <SelectItem value="247">24/7 (+18%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Escalation policy<InfoTip>Fixed-price = contractor takes inflation risk; Indexed = passes through; Cost-plus = pays actuals.</InfoTip></Label>
                <Select value={form.escalation_policy} onValueChange={v => setField('escalation_policy', v)}>
                  <SelectTrigger className="h-9" data-testid="const-escalation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed_price">Fixed price (+2%)</SelectItem>
                    <SelectItem value="indexed">Indexed (0%)</SelectItem>
                    <SelectItem value="cost_plus">Cost-plus (−1%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>BIM LOD<InfoTip>Higher LOD = richer model, fewer field surprises but more upfront design cost.</InfoTip></Label>
                <Select value={form.bim_lod} onValueChange={v => setField('bim_lod', v)}>
                  <SelectTrigger className="h-9" data-testid="const-bim-lod"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOD200">LOD 200 (−1%)</SelectItem>
                    <SelectItem value="LOD300">LOD 300 (0%)</SelectItem>
                    <SelectItem value="LOD350">LOD 350 (+1.5%)</SelectItem>
                    <SelectItem value="LOD400">LOD 400 (+3%)</SelectItem>
                    <SelectItem value="LOD500">LOD 500 (+5%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Procurement model<InfoTip>Design-Build/IPD compress cost; EPC/PPP add risk premium.</InfoTip></Label>
                <Select value={form.procurement_model} onValueChange={v => setField('procurement_model', v)}>
                  <SelectTrigger className="h-9" data-testid="const-procurement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="traditional">Traditional (0%)</SelectItem>
                    <SelectItem value="design_build">Design-Build (−2%)</SelectItem>
                    <SelectItem value="construction_management">Construction Mgmt (+2%)</SelectItem>
                    <SelectItem value="epc">EPC (+4%)</SelectItem>
                    <SelectItem value="ipd">IPD (−3%)</SelectItem>
                    <SelectItem value="ppp">PPP (+5%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Construction phases &amp; sub-phases included
              <InfoTip>Toggle entire phases on/off, or expand a phase to disable individual sub-phases. Disabling a sub-phase shrinks the phase cost and labour proportionally.</InfoTip>
            </Label>
            <div className="space-y-1.5" data-testid="phases-tree">
              {phases.map((p, idx) => {
                const on = form.phases_enabled.includes(p.key);
                const wbs = wbsLibrary[p.key] || [];
                return (
                  <PhaseTreeRow
                    key={p.key}
                    phase={p}
                    on={on}
                    onToggle={() => togglePhase(p.key)}
                    wbs={wbs}
                    disabled={form.sub_phases_disabled}
                    onToggleSub={toggleSubPhase}
                    color={PHASE_COLORS[idx % PHASE_COLORS.length]}
                  />
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DrawingImport onApply={applyDrawingResult} />
            <AmenitiesPicker
              selected={form.amenities}
              onChange={(arr) => setField('amenities', arr)}
              sector={form.sector}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={runCalc} disabled={loading} className="bg-orange-600 hover:bg-orange-700" data-testid="const-calculate-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calculating…</> : <><CalcIcon className="h-4 w-4 mr-2" />Calculate Construction Estimate</>}
            </Button>
            {result && (
              <>
                <Button variant="outline" onClick={saveProject} disabled={saving} data-testid="const-save-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Save className="h-4 w-4 mr-2" />Save Project</>}
                </Button>
                <PrintButton testid="calc-print-btn" />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {result && <ConstructionResults result={result} />}
    </div>
  );
}

function ConstructionResults({ result }) {
  const symbol = result.forex?.symbol || '$';
  const rate = result.forex?.rate_per_usd || 1;
  const fmt = (usd) => {
    const tgt = (usd || 0) * rate;
    return `${symbol}${tgt.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const phasePieData = result.phases.map((p, i) => ({
    name: p.phase_label, value: p.cost_usd, fill: PHASE_COLORS[i % PHASE_COLORS.length],
  }));
  const phaseBarData = result.phases.map(p => ({
    phase: p.phase_label.split(' ')[0],
    Labour: p.labour_cost_usd,
    Materials: p.material_cost_usd,
    Hours: p.labour_hours,
  }));
  const topTrades = result.labour_summary.by_trade.slice(0, 10);

  return (
    <div className="space-y-4" data-testid="construction-results">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Total Labour Hours" value={formatHours(result.total_labour_hours)} icon={<Wrench className="h-4 w-4" />} accent="bg-blue-600" />
        <KpiCard label="Direct Cost (USD)" value={formatCurrency(result.total_direct_cost_usd, '$')} icon={<Package className="h-4 w-4" />} accent="bg-indigo-600" />
        <KpiCard label="Final Cost (USD)" value={formatCurrency(result.final_cost_usd, '$')} icon={<TrendingUp className="h-4 w-4" />} accent="bg-emerald-600" />
        <KpiCard label={`Final (${result.forex.target_currency})`} value={`${symbol}${Number(result.forex.final_cost_target).toLocaleString(undefined,{maximumFractionDigits:0})}`} icon={<Building2 className="h-4 w-4" />} accent="bg-orange-600" />
        <KpiCard label="Avg Workforce" value={`${result.indicative_avg_workforce} pax`} icon={<HardHat className="h-4 w-4" />} accent="bg-rose-600" />
      </div>

      {result.forex.is_indicative && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
          <strong>Indicative forex:</strong> 1 USD = {result.forex.rate_per_usd} {result.forex.target_currency}. Rates are editable in the Forex tab.
        </div>
      )}

      <Tabs defaultValue="phases">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="phases" data-testid="const-tab-phases">Phases</TabsTrigger>
          <TabsTrigger value="labour" data-testid="const-tab-labour">Labour</TabsTrigger>
          <TabsTrigger value="materials" data-testid="const-tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="const-tab-commercial">Commercial</TabsTrigger>
        </TabsList>

        <TabsContent value="phases" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card><CardHeader><CardTitle className="text-base">Cost split by phase</CardTitle></CardHeader><CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={phasePieData} dataKey="value" nameKey="name" outerRadius={100} label={(e) => `${(e.percent * 100).toFixed(0)}%`}>
                    {phasePieData.map((_, i) => <Cell key={i} fill={PHASE_COLORS[i % PHASE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent></Card>

            <Card><CardHeader><CardTitle className="text-base">Labour vs materials (USD)</CardTitle></CardHeader><CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={phaseBarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phase" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v) => formatCurrency(v, '$')} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Labour" stackId="a" fill="#6366f1" />
                  <Bar dataKey="Materials" stackId="a" fill="#f97316" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent></Card>
          </div>

          <Card><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="text-left p-3 w-8"></th>
                    <th className="text-left p-3">Phase / Sub-phase</th>
                    <th className="text-right p-3">Weight</th>
                    <th className="text-right p-3">Hours</th>
                    <th className="text-right p-3">Labour Cost</th>
                    <th className="text-right p-3">Material Cost</th>
                    <th className="text-right p-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {result.phases.map(p => (
                    <PhaseRow key={p.phase} phase={p} fmt={fmt} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="labour" className="space-y-4">
          <Card><CardHeader><CardTitle className="text-base">Top trades by hours</CardTitle></CardHeader><CardContent>
            <ResponsiveContainer width="100%" height={Math.max(240, topTrades.length * 30)}>
              <BarChart data={topTrades} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="description" fontSize={11} width={160} />
                <Tooltip formatter={(v) => formatHours(v) + ' hrs'} />
                <Bar dataKey="hours" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Trade</th>
                    <th className="text-left p-3">Group</th>
                    <th className="text-right p-3">Hours</th>
                    <th className="text-right p-3">Rate</th>
                    <th className="text-right p-3">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {result.labour_summary.by_trade.map(t => (
                    <tr key={t.item_code} className="border-t hover:bg-slate-50">
                      <td className="p-3">{t.description}</td>
                      <td className="p-3"><Badge variant="outline" className="capitalize">{t.trade_group}</Badge></td>
                      <td className="text-right p-3">{formatHours(t.hours)}</td>
                      <td className="text-right p-3">${t.rate_usd}/hr</td>
                      <td className="text-right p-3 font-medium">{fmt(t.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="materials" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card><CardHeader><CardTitle className="text-base">Material cost by category</CardTitle></CardHeader><CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={Object.entries(result.material_summary.by_category).map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" outerRadius={100} label={(e) => e.name}>
                    {Object.entries(result.material_summary.by_category).map((_, i) => <Cell key={i} fill={PHASE_COLORS[i % PHASE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent></Card>

            <Card><CardHeader><CardTitle className="text-base">Total material spend</CardTitle></CardHeader><CardContent>
              <div className="text-3xl font-bold">{fmt(result.material_summary.total_cost_usd)}</div>
              <p className="text-xs text-slate-500 mt-1">Includes direct material cost. Overheads, risk and margin are applied in Commercial.</p>
              <ul className="mt-4 space-y-1 text-sm">
                {Object.entries(result.material_summary.by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <li key={k} className="flex justify-between border-b border-slate-100 py-1">
                    <span className="capitalize text-slate-700">{k.replace('_', ' ')}</span>
                    <span className="font-medium">{fmt(v)}</span>
                  </li>
                ))}
              </ul>
            </CardContent></Card>
          </div>
        </TabsContent>

        <TabsContent value="commercial" className="space-y-4">
          <Card><CardHeader><CardTitle className="text-base">Cost build-up</CardTitle></CardHeader><CardContent>
            <table className="w-full text-sm">
              <tbody>
                {[
                  ['Direct cost (labour + materials)', result.contract.base_cost_usd, null],
                  ...(result.amenities_cost_usd > 0 ? [[`Amenities (${(result.amenities || []).length} item${(result.amenities || []).length > 1 ? 's' : ''})`, result.amenities_cost_usd, null]] : []),
                  ...(result.project_detail_uplift_usd ? [[`Project-detail uplift (${result.project_detail_uplift_pct >= 0 ? '+' : ''}${result.project_detail_uplift_pct}%) — site/shift/escalation/LOD/procurement`, result.project_detail_uplift_usd, null]] : []),
                  ...(result.inflation_uplift_usd > 0 ? [[`Inflation uplift (incl. in direct cost — ${result.annual_inflation_pct}%/yr × ½ duration ≈ ${result.inflation_uplift_pct}%)`, result.inflation_uplift_usd, null]] : []),
                  [`Criticality contingency (${result.criticality}, ${(result.contract.criticality_contingency_pct * 100).toFixed(0)}%)`, result.contract.criticality_contingency_usd, null],
                  [`Overhead (${(result.contract.overhead_pct * 100).toFixed(1)}%)`, result.contract.overhead_usd, null],
                  [`Contract risk (${(result.contract.risk_pct * 100).toFixed(1)}%)`, result.contract.risk_usd, null],
                  [`Target margin (${(result.contract.margin_pct * 100).toFixed(1)}%)`, result.contract.margin_usd, null],
                ].map(([label, v], idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-2">{label}</td>
                    <td className="text-right py-2">{fmt(v)}</td>
                  </tr>
                ))}
                <tr className="font-bold text-lg">
                  <td className="py-3">Final cost ({result.contract.contract_label})</td>
                  <td className="text-right py-3 text-emerald-700">{fmt(result.final_cost_usd)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-slate-700">
                <FileSpreadsheet className="h-4 w-4" />
                Indicative forex — 1 USD = {result.forex.rate_per_usd} {result.forex.target_currency}
              </div>
              <div className="mt-1 text-slate-600">
                Final in {result.forex.target_currency}: <span className="font-semibold">{symbol}{Number(result.forex.final_cost_target).toLocaleString()}</span>
              </div>
              {(result.city || result.effective_cost_multiplier) && (
                <div className="mt-1 text-xs text-slate-500">
                  Effective cost multiplier applied: <span className="font-mono">×{result.effective_cost_multiplier}</span>
                  {result.city && <> (region {result.region} + city {result.city})</>}
                </div>
              )}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PhaseTreeRow({ phase, on, onToggle, wbs, disabled, onToggleSub, color }) {
  const [open, setOpen] = useState(false);
  const phaseDisabledCount = wbs.filter(w => disabled.includes(`${phase.key}:${w.key}`)).length;
  return (
    <div className={`rounded-md border ${on ? 'border-orange-300 bg-orange-50/40' : 'border-slate-200 bg-slate-50/30 opacity-60'}`}>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="text-slate-500 hover:text-slate-900"
          data-testid={`const-phase-toggle-${phase.key}`}
          aria-label="Expand sub-phases"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={onToggle}
          className={`text-sm px-3 py-1 rounded-full border transition-all flex-1 text-left ${on
            ? 'bg-orange-600 text-white border-orange-600'
            : 'bg-white text-slate-700 border-slate-300'}`}
          style={on ? { boxShadow: `inset 0 -2px 0 ${color}` } : {}}
          data-testid={`const-phase-${phase.key}`}
        >
          {phase.label}
          {wbs.length > 0 && <span className="ml-2 text-[10px] opacity-80">{wbs.length} sub-phases{phaseDisabledCount > 0 ? ` · ${phaseDisabledCount} off` : ''}</span>}
        </button>
      </div>
      {open && wbs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 px-3 pb-2 pt-1 border-t border-slate-200">
          {wbs.map(w => {
            const id = `${phase.key}:${w.key}`;
            const off = disabled.includes(id);
            return (
              <button
                key={w.key}
                type="button"
                onClick={() => onToggleSub(phase.key, w.key)}
                disabled={!on}
                className={`text-left text-xs px-2.5 py-1.5 rounded border transition-all ${off
                  ? 'bg-white text-slate-400 border-slate-200 line-through'
                  : 'bg-orange-100/80 text-slate-800 border-orange-200 hover:bg-orange-200'}`}
                data-testid={`const-subphase-${phase.key}-${w.key}`}
                title={off ? 'Click to re-enable' : 'Click to disable'}
              >
                <span className="font-medium">{w.label}</span>
                {w.weight ? <span className="ml-1 text-[10px] opacity-70">·{(w.weight * 100).toFixed(0)}%</span> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PhaseRow({ phase, fmt }) {
  const [open, setOpen] = useState(false);
  const wbs = phase.wbs || [];
  const hasWbs = wbs.length > 0;
  return (
    <>
      <tr className="border-t hover:bg-slate-50">
        <td className="p-3 align-top">
          {hasWbs ? (
            <button
              onClick={() => setOpen(o => !o)}
              className="text-slate-500 hover:text-slate-900"
              data-testid={`phase-toggle-${phase.phase}`}
              aria-label="Toggle sub-phases"
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : <span className="text-slate-300">–</span>}
        </td>
        <td className="p-3 font-medium text-slate-900">
          {phase.phase_label}
          {hasWbs && <span className="ml-2 text-xs text-slate-500">{wbs.length} sub-phases</span>}
        </td>
        <td className="text-right p-3">{(phase.weight * 100).toFixed(1)}%</td>
        <td className="text-right p-3">{formatHours(phase.labour_hours)}</td>
        <td className="text-right p-3">{fmt(phase.labour_cost_usd)}</td>
        <td className="text-right p-3">{fmt(phase.material_cost_usd)}</td>
        <td className="text-right p-3 font-semibold">{fmt(phase.cost_usd)}</td>
      </tr>
      {open && wbs.map(w => (
        <tr key={`${phase.phase}-${w.key}`} className="bg-slate-50/60 border-t border-slate-100" data-testid={`subphase-${phase.phase}-${w.key}`}>
          <td className="p-2"></td>
          <td className="p-2 pl-8 text-sm text-slate-700">
            <span className="text-slate-400 mr-1.5">↳</span>{w.label}
            {w.primary_trades?.length > 0 && (
              <span className="ml-2 text-[10px] text-slate-500">{w.primary_trades.join(' · ')}</span>
            )}
          </td>
          <td className="text-right p-2 text-xs text-slate-600">{(w.weight * 100).toFixed(1)}%</td>
          <td className="text-right p-2 text-xs text-slate-700">{formatHours(w.labour_hours)}</td>
          <td colSpan={2}></td>
          <td className="text-right p-2 text-sm font-medium text-slate-700">{fmt(w.cost_usd)}</td>
        </tr>
      ))}
    </>
  );
}

function KpiCard({ label, value, icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg text-white ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}
